import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { Visit } from '../types';
import { useAuth } from '../contexts/AuthContext';
import {
  Users,
  MapPin,
  Plus,
  FileText,
  Calendar,
  ChevronRight,
  Store,
  CheckCircle2,
  CalendarDays,
  BarChart3,
  Trophy,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { UserAvatar } from '../components/shared/UserAvatar';
import { formatDate } from '../utils/datetime';

interface VisitStats {
  visits_today: number;
  visits_last_7_days: number;
  visits_this_month: number;
  visits_total: number;
  placements_secured_this_month: number;
  placements_secured_total: number;
}

const emptyStats: VisitStats = {
  visits_today: 0,
  visits_last_7_days: 0,
  visits_this_month: 0,
  visits_total: 0,
  placements_secured_this_month: 0,
  placements_secured_total: 0,
};

interface TopEntry {
  user_id: string;
  full_name: string;
  photo_url: string | null;
  score: number;
  rank: number;
}

const PERSONAL_METRICS: Array<{
  key: keyof Pick<
    VisitStats,
    'visits_today' | 'visits_last_7_days' | 'visits_this_month' | 'visits_total'
  >;
  label: string;
  emphasize?: boolean;
}> = [
  { key: 'visits_today', label: 'Today', emphasize: true },
  { key: 'visits_last_7_days', label: '7 days' },
  { key: 'visits_this_month', label: 'Month' },
  { key: 'visits_total', label: 'Total' },
];

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [recentVisits, setRecentVisits] = useState<Visit[]>([]);
  const [teamStats, setTeamStats] = useState<VisitStats>(emptyStats);
  const [personalStats, setPersonalStats] = useState<VisitStats>(emptyStats);
  const [totalPoS, setTotalPoS] = useState(0);
  const [topThree, setTopThree] = useState<TopEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const requests: Array<Promise<unknown>> = [
        api.get('/visits?limit=5'),
        api.get('/visits/stats'),
        api.get('/pos?limit=1'),
        api.get('/visits/leaderboard?period=month'),
      ];
      if (user?.id) {
        requests.push(api.get(`/visits/stats?user_id=${user.id}`));
      }

      const settled = await Promise.allSettled(requests);
      const [visitsSettled, statsSettled, posSettled, lbSettled, personalSettled] =
        settled;

      if (visitsSettled.status === 'fulfilled') {
        const res = visitsSettled.value as { data?: { data?: Visit[] } };
        setRecentVisits(res.data?.data || []);
      }
      if (statsSettled.status === 'fulfilled') {
        const res = statsSettled.value as { data?: Partial<VisitStats> };
        setTeamStats({ ...emptyStats, ...(res.data || {}) });
      }
      if (posSettled.status === 'fulfilled') {
        const res = posSettled.value as { data?: { total?: number } };
        setTotalPoS(res.data?.total || 0);
      }
      if (lbSettled.status === 'fulfilled') {
        const res = lbSettled.value as { data?: { entries?: TopEntry[] } };
        setTopThree((res.data?.entries || []).slice(0, 3));
      }
      if (personalSettled?.status === 'fulfilled') {
        const res = personalSettled.value as { data?: Partial<VisitStats> };
        setPersonalStats({ ...emptyStats, ...(res.data || {}) });
      }

      setLoading(false);
    };

    fetchData();
  }, [user?.id]);

  const QuickActionCard = ({ to, icon: Icon, title, description, colorClass }: any) => (
    <Link
      to={to}
      className="group relative bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200 flex flex-col items-center text-center overflow-hidden"
    >
      <div className={`absolute top-0 left-0 w-full h-1 ${colorClass}`} />
      <div
        className={`p-3 rounded-full mb-4 ${colorClass
          .replace('bg-', 'bg-opacity-10 text-')
          .replace('600', '700')}`}
      >
        <Icon size={24} />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500">{description}</p>
    </Link>
  );

  const StatCard = ({ title, value, icon: Icon }: any) => (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <div className="p-2 bg-brand-50 rounded-lg text-brand-600">
          <Icon size={20} />
        </div>
      </div>
      <h3 className="text-sm font-medium text-gray-500 mb-1">{title}</h3>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );

  const display = (n: number) => (loading ? '...' : n);

  const todayShare =
    teamStats.visits_today > 0
      ? Math.min(100, Math.round((personalStats.visits_today / teamStats.visits_today) * 100))
      : personalStats.visits_today > 0
        ? 100
        : 0;

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {user?.fullName?.split(' ')[0]}
          </h1>
          <p className="text-gray-500">Your day and the team at a glance.</p>
        </div>
        <div className="text-sm text-gray-500 bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm flex items-center">
          <Calendar size={16} className="mr-2 text-brand-600" />
          {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </div>
      </div>

      {user && (
        <div className="rounded-xl border border-brand-100 bg-brand-50/70 px-4 py-4 sm:px-5 sm:py-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <Link
              to="/profile"
              className="flex items-center gap-3 shrink-0 group"
            >
              <UserAvatar
                userId={user.id}
                fullName={user.fullName}
                photoUrl={user.photoUrl}
                size="md"
              />
              <div>
                <p className="text-sm font-semibold text-gray-900 group-hover:text-brand-700">
                  Your visits
                </p>
                <p className="text-xs text-gray-500">Personal · vs team today</p>
              </div>
            </Link>

            <div className="flex-1 min-w-0">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-0 sm:divide-x sm:divide-brand-100/80">
                {PERSONAL_METRICS.map(({ key, label, emphasize }) => (
                  <div
                    key={key}
                    className="sm:px-4 first:sm:pl-0 last:sm:pr-0 text-center sm:text-left"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-700/70">
                      {label}
                    </p>
                    <p
                      className={
                        emphasize
                          ? 'text-3xl font-black text-brand-800 tabular-nums leading-tight'
                          : 'text-2xl font-bold text-gray-900 tabular-nums leading-tight'
                      }
                    >
                      {display(personalStats[key])}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-3 sm:mt-4">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>
                    Today: you {display(personalStats.visits_today)} / team{' '}
                    {display(teamStats.visits_today)}
                  </span>
                  {!loading && <span>{todayShare}%</span>}
                </div>
                <div className="h-1.5 rounded-full bg-white/80 border border-brand-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-brand-600 transition-all duration-500"
                    style={{ width: loading ? '0%' : `${todayShare}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Team</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <StatCard title="Today" value={display(teamStats.visits_today)} icon={CalendarDays} />
          <StatCard
            title="Last 7 days"
            value={display(teamStats.visits_last_7_days)}
            icon={MapPin}
          />
          <StatCard
            title="This month"
            value={display(teamStats.visits_this_month)}
            icon={BarChart3}
          />
          <StatCard title="Total visits" value={display(teamStats.visits_total)} icon={Store} />
          <StatCard
            title="Placements this month"
            value={display(teamStats.placements_secured_this_month)}
            icon={CheckCircle2}
          />
          <StatCard
            title="Placements total"
            value={display(teamStats.placements_secured_total)}
            icon={CheckCircle2}
          />
          <StatCard title="Total PoS" value={display(totalPoS)} icon={Users} />
        </div>
      </div>

      {topThree.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Trophy size={20} className="text-amber-500" />
              Top this month
            </h2>
            <Link
              to="/leaderboard"
              className="text-sm font-medium text-brand-600 hover:text-brand-700 flex items-center"
            >
              Full leaderboard <ChevronRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {topThree.map((entry) => (
              <Link
                key={entry.user_id}
                to={`/leaderboard/${entry.user_id}`}
                className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2"
              >
                <span className="text-sm font-bold text-gray-400 w-6">#{entry.rank}</span>
                <UserAvatar
                  userId={entry.user_id}
                  fullName={entry.full_name}
                  photoUrl={entry.photo_url}
                  size="sm"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{entry.full_name}</p>
                  <p className="text-xs text-gray-500">Score {entry.score}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickActionCard
            to="/visits/new"
            icon={MapPin}
            title="Log Visit"
            description="Record a new store visit"
            colorClass="bg-brand-600"
          />
          <QuickActionCard
            to="/pos/new"
            icon={Plus}
            title="Add PoS"
            description="Register a new store"
            colorClass="bg-blue-600"
          />
          <QuickActionCard
            to="/pos"
            icon={Store}
            title="Points of Sale"
            description="Browse your territory"
            colorClass="bg-purple-600"
          />
          <QuickActionCard
            to="/reports"
            icon={FileText}
            title="Reports"
            description="View performance analytics"
            colorClass="bg-orange-600"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
          <Link
            to="/visits"
            className="text-sm font-medium text-brand-600 hover:text-brand-700 flex items-center"
          >
            View all <ChevronRight size={16} />
          </Link>
        </div>
        <div className="divide-y divide-gray-100">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading activity...</div>
          ) : recentVisits.length > 0 ? (
            recentVisits.map((visit) => (
              <div
                key={visit.id}
                className="px-6 py-4 hover:bg-gray-50 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center">
                  <div
                    className={`p-2 rounded-full mr-4 ${
                      visit.store_type === 'supermarket'
                        ? 'bg-blue-100 text-blue-600'
                        : visit.store_type === 'spaeti'
                          ? 'bg-purple-100 text-purple-600'
                          : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    <Store size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {(visit as any).customer_name || (visit as any).pos_name}
                    </p>
                    <p className="text-xs text-gray-500 capitalize">
                      {visit.store_type} •{' '}
                      {formatDate(visit.visit_date)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Completed
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-gray-500">
              No recent visits found. Start your route!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
